/**
 * A barra do tocador: a vista do que o `nucleo/tocador.tsx` está tocando.
 *
 * Mora na raiz, junto com a tarja de cinema, e não dentro da moldura: metade
 * das telas da casa -- cinema, livros, jardim, os jogos -- fica fora da moldura,
 * e com a barra lá dentro a música tocava nessas telas sem nada para pausá-la.
 *
 * Fica dentro da porta de senha, que é onde a raiz do app já a colocou: uma
 * barra de música na tela da senha não teria o que fazer.
 *
 * De quanto ela sobe do fim da janela quem decide é o CSS, pela marca
 * `com-moldura` que a moldura põe no `body` enquanto está em cena: com moldura
 * há uma navegação embaixo de que desviar, sem moldura não há.
 */

import { useEffect, useState } from 'react';
import { relogio, traduzir } from '@nosso-sempre/shared';
import { usarTocador } from '../nucleo/tocador';
import { Folha } from './Folha';
import { Icone } from './Icone';
import './barraDoTocador.css';

export function BarraDoTocador() {
  const t = usarTocador();
  /** Se a folha com o repertório do item está aberta. */
  const [lista, setLista] = useState(false);

  /* Item novo -- ou tocador fechado -- fecha a folha. Sem isto o `lista` ficava
     presa em `true` depois de parar, e a folha abria sozinha na gravação
     seguinte, que é o tipo de coisa que ninguém consegue explicar depois. */
  const iaId = t.atual?.gravacao.iaId ?? '';
  useEffect(() => {
    setLista(false);
  }, [iaId]);

  if (!t.atual) return null;

  const fim = t.duracaoMs > 0 ? t.duracaoMs : 0;
  const agora = Math.min(t.posicaoMs, fim || t.posicaoMs);
  /* Um 78 do Great 78 Project tem uma faixa só, e para essa não há "próxima":
     os dois botões não aparecem e a barra fica a de sempre. O show do `etree`
     e a coletânea de netlabel é que ganham por onde andar. */
  const faixas = t.atual.gravacao.faixas;
  const disco = faixas.length > 1;
  const conta = traduzir('{n} de {total}', { n: t.naFaixa + 1, total: faixas.length });
  /* Mudo não é um estado à parte: é o volume no chão. Ver `musica/volume.ts`. */
  const mudo = t.volume === 0;

  return (
    <section
      className={disco ? 'tocador com-faixas' : 'tocador'}
      aria-label={traduzir('Tocando agora')}
    >
      <div className="tocador-capa" aria-hidden="true">
        {t.atual.capaUrl ? (
          <img src={t.atual.capaUrl} alt="" width={44} height={44} />
        ) : (
          <Icone nome="nota-musical" tamanho={22} />
        )}
      </div>

      <div className="tocador-nomes">
        <strong>{t.atual.titulo}</strong>
        <div className="tocador-quem">
          <span className="discreto">{t.atual.artista}</span>
          {disco ? (
            <button
              className="tocador-conta"
              type="button"
              onClick={() => setLista(true)}
              title={traduzir('Ver as faixas')}
              /* O que se vê é "3 de 20", que sozinho não diz que abre nada; o
                 que se ouve começa por ele mesmo assim, senão o comando de voz
                 pelo rótulo visível deixa de achar o botão. */
              aria-label={`${conta}, ${traduzir('Ver as faixas')}`}
            >
              {conta}
            </button>
          ) : null}
        </div>
        {t.erro ? (
          <span className="tocador-erro" role="status">
            {t.erro}
          </span>
        ) : null}
      </div>

      <div className="tocador-tempo">
        <span>{relogio(agora)}</span>
        <label className="so-leitor" htmlFor="tocador-linha">
          {traduzir('Onde a faixa está')}
        </label>
        <input
          id="tocador-linha"
          className="tocador-linha"
          type="range"
          min={0}
          max={fim > 0 ? fim : 1}
          step={1000}
          value={agora}
          disabled={fim <= 0}
          onChange={(e) => t.correr(Number(e.target.value))}
        />
        <span>{fim > 0 ? relogio(fim) : '--:--'}</span>
      </div>

      {/* Os três do meio dizem a tecla no balão -- espaço pausa, as setas
          correm quinze segundos, e quem liga isso é o `useEffect` do teclado
          lá no `nucleo/tocador.tsx`. A tecla vai só no `title`: o `rotulo`,
          que é o nome que o leitor de tela anuncia, fica sem ela. Ouvir
          "Pausar parênteses espaço" a cada foco cansa, e quem usa leitor de
          tela não precisa que lhe contem onde fica a barra de espaço. */}
      <div className="tocador-botoes">
        {disco ? (
          <button
            className="icone-botao"
            type="button"
            onClick={() => t.pularFaixa(-1)}
            title={traduzir('Faixa anterior')}
          >
            <Icone nome="pular-atras" tamanho={20} rotulo={traduzir('Faixa anterior')} />
          </button>
        ) : null}
        <button
          className="icone-botao"
          type="button"
          onClick={() => t.pular(-15)}
          title={traduzir('Voltar 15 segundos (←)')}
        >
          {/* A barra com o triângulo troca de faixa; os dois triângulos correm
              dentro da faixa. Este é o de correr, e por anos foi o desenho
              errado -- ver `theme/desenhos.ts`. */}
          <Icone nome="correr-atras" tamanho={20} rotulo={traduzir('Voltar 15 segundos')} />
        </button>
        <button
          className="tocador-tocar"
          type="button"
          onClick={t.alternar}
          title={t.tocando ? traduzir('Pausar (espaço)') : traduzir('Tocar (espaço)')}
        >
          {t.abrindo ? (
            <span className="rodando" role="status" aria-label={traduzir('Abrindo a gravação')} />
          ) : (
            <Icone
              nome={t.tocando ? 'pausar' : 'tocar'}
              tamanho={22}
              rotulo={t.tocando ? traduzir('Pausar') : traduzir('Tocar')}
            />
          )}
        </button>
        <button
          className="icone-botao"
          type="button"
          onClick={() => t.pular(15)}
          title={traduzir('Adiantar 15 segundos (→)')}
        >
          <Icone nome="correr-adiante" tamanho={20} rotulo={traduzir('Adiantar 15 segundos')} />
        </button>
        {disco ? (
          <button
            className="icone-botao"
            type="button"
            onClick={() => t.pularFaixa(1)}
            title={traduzir('Próxima faixa')}
          >
            <Icone nome="pular-adiante" tamanho={20} rotulo={traduzir('Próxima faixa')} />
          </button>
        ) : null}
        <button
          className="icone-botao"
          type="button"
          onClick={t.parar}
          title={traduzir('Fechar o tocador')}
        >
          <Icone nome="fechar" tamanho={18} rotulo={traduzir('Fechar o tocador')} />
        </button>
      </div>

      {/* A altura, que o CSS esconde no telefone -- e não por caber mal: no
          Safari do iPhone o volume não se mexe por programa, então a barra
          seria um controle sem efeito. No telefone quem manda são os botões
          da lateral, e aqui manda esta. */}
      <div className="tocador-som">
        <button
          className="icone-botao"
          type="button"
          onClick={t.alternarMudo}
          title={mudo ? traduzir('Ligar o som (M)') : traduzir('Tirar o som (M)')}
        >
          <Icone
            nome={mudo ? 'mudo' : 'som'}
            tamanho={18}
            rotulo={mudo ? traduzir('Ligar o som') : traduzir('Tirar o som')}
          />
        </button>
        <label className="so-leitor" htmlFor="tocador-volume">
          {traduzir('Volume')}
        </label>
        <input
          id="tocador-volume"
          className="tocador-volume"
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={t.volume}
          /* Sem isto o leitor de tela lê "0,53", que não quer dizer nada. A
             conta não tem letra nenhuma, então não é frase a traduzir. */
          aria-valuetext={`${Math.round(t.volume * 100)}%`}
          onChange={(e) => t.mudarVolume(Number(e.target.value))}
        />
      </div>

      {/* A folha mora dentro da barra e mesmo assim não é presa por ela: um
          `<dialog>` aberto com `showModal` sobe para a camada de cima da
          janela, acima do `backdrop-filter` e do `z-index` daqui. */}
      <Folha
        aberta={lista}
        aoFechar={() => setLista(false)}
        titulo={traduzir('As faixas')}
        recado={t.atual.gravacao.album}
        alta
      >
        {/* Componente à parte porque a `Folha` só monta os filhos enquanto está
            aberta, e a barra se remonta quatro vezes por segundo com o relógio:
            montar vinte linhas em cada uma dessas voltas seria perda pura. */}
        <Faixas aoEscolher={() => setLista(false)} />
      </Folha>
    </section>
  );
}

/**
 * O repertório do item, dentro da folha: onde o tocador está e para onde pode ir.
 *
 * Lê o tocador de novo em vez de receber tudo por propriedade porque está sob o
 * mesmo provedor -- e assim a barra passa um único elemento por volta de
 * relógio, em vez de remontar a lista inteira que a folha fechada nem mostra.
 */
function Faixas({ aoEscolher }: { aoEscolher: () => void }) {
  const t = usarTocador();
  if (!t.atual) return null;

  return (
    <div className="tocador-faixas">
      {t.atual.gravacao.faixas.map((f, i) => {
        const aqui = i === t.naFaixa;
        return (
          <button
            key={`${f.numero}-${f.url}`}
            className={aqui ? 'tocador-faixa aqui' : 'tocador-faixa'}
            type="button"
            aria-current={aqui ? 'true' : undefined}
            onClick={() => {
              /* Na que já está tocando é só fechar: `pularFaixa(0)` passaria
                 pela guarda e reabriria a faixa do começo, tirando o lugar de
                 quem só queria conferir o repertório. */
              if (!aqui) t.pularFaixa(i - t.naFaixa);
              aoEscolher();
            }}
          >
            <span className="tocador-faixa-numero">
              {aqui ? <Icone nome="onda" tamanho={16} /> : f.numero}
            </span>
            <span className="tocador-faixa-nome">{f.titulo}</span>
            {/* O `length` do arquivo falta em parte do acervo, e aí o
                `duracaoMs` vem zero: melhor nenhum tempo que `0:00`. */}
            {f.duracaoMs > 0 ? (
              <span className="tocador-faixa-tempo">{relogio(f.duracaoMs)}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
